import React, { useState, useRef } from 'react';
import {
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Box,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import Papa from 'papaparse';
import InventoryCategoryAPIs from '../../utilities/api/category/AdminInventoryCategoryList';
import InventoryLocationAPIs from '../../utilities/api/location/AdminLocationList';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InfoIcon from '@mui/icons-material/Info';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DownloadIcon from '@mui/icons-material/Download';
import { useSelector } from 'react-redux';
import { extractBulkInventoryValidationErrorMessage } from '../../utils/helper';
import { InfoOutlined } from '@mui/icons-material';

// Equipment CSV Import Schema Constants
const equipmentSchemaFields = [
  'name',
  'upc',
  'manufacturer',
  'description',
  'location',
  'instances',
  'availability',
  'status',
  'supervised',
  'purchase_cost',
  'category',
  'purchase_date',
  'warranty_expiration',
];

const equipmentSchemaDescription = {
  name: { desc: 'Machine name/type', mandatory: true, options: 'Text' },
  upc: { desc: 'Universal Product Code', mandatory: false, options: 'Number' },
  manufacturer: {
    desc: 'Manufacturer name',
    mandatory: false,
    options: 'Text',
  },
  description: {
    desc: 'Machine description',
    mandatory: false,
    options: 'Text',
  },
  location: { desc: 'Machine location', mandatory: false, options: 'Text' },
  instances: {
    desc: 'Number of instances',
    mandatory: true,
    options: 'Number',
  },
  availability: {
    desc: 'Availability level',
    mandatory: false,
    options: 'Moderate, Very Low, Low, High, Always',
  },
  status: {
    desc: 'Machine status',
    mandatory: false,
    options: 'Operational, Under Maintenance, Out of Service',
  },
  supervised: {
    desc: 'Supervision requirement',
    mandatory: false,
    options: 'TRUE, FALSE',
  },
  purchase_cost: { desc: 'Purchase cost', mandatory: false, options: 'Number' },
  category: { desc: 'Machine category', mandatory: false, options: 'Text' },
  purchase_date: { desc: 'Purchase date', mandatory: false, options: 'Date' },
  warranty_expiration: {
    desc: 'Warranty expiration date',
    mandatory: false,
    options: 'Date',
  },
};

const availabilityMapping = {
  moderate: '0',
  'very low': '1',
  low: '2',
  high: '3',
  always: '4',
};

const statusMapping = {
  operational: '0',
  'under maintenance': '1',
  'out of service': '2',
};

const EquipmentImport = ({ user, allUsersData, onImportComplete }) => {
  // State for CSV Import
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isMappingDialogOpen, setIsMappingDialogOpen] = useState(false);
  const [isResultDialogOpen, setIsResultDialogOpen] = useState(false);
  const [isInfoDialogOpen, setIsInfoDialogOpen] = useState(false);
  const [csvData, setCsvData] = useState({ headers: [], rows: [] });
  const [mapping, setMapping] = useState({});
  const [errorEntries, setErrorEntries] = useState([]);
  const [successfulImports, setSuccessfulImports] = useState(0);
  const [totalEntries, setTotalEntries] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [localCategories, setLocalCategories] = useState([]);
  const [localLocations, setLocalLocations] = useState([]);
  const fileInputRef = useRef(null);

  // Get categories and locations from Redux
  const categories = useSelector(state => state.category.category_list);
  const locations = useSelector(state => state.location.location_list);

  // Initialize local categories and locations from Redux
  React.useEffect(() => {
    if (categories && Array.isArray(categories)) {
      setLocalCategories(categories.map(cat => cat.name || cat));
    }
    if (locations && Array.isArray(locations)) {
      setLocalLocations(locations.map(loc => loc.name || loc));
    }
  }, [categories, locations]);

  // Import process steps
  const steps = ['Select CSV File', 'Map Fields', 'Import Results'];

  const handleOpenImportDialog = () => {
    setIsImportDialogOpen(true);
    setActiveStep(0);
  };

  const handleCloseImportDialog = () => {
    setIsImportDialogOpen(false);
    setCsvData({ headers: [], rows: [] });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = event => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      Papa.parse(file, {
        header: false,
        skipEmptyLines: true,
        complete: results => {
          if (results.data && results.data.length >= 2) {
            const headers = results.data[0].map(header =>
              header.trim().toLowerCase()
            );

            // Remove empty rows (all cells blank)
            const cleanedRows = results.data
              .slice(1)
              .map(row => row.map(cell => cell.trim()))
              .filter(row => row.some(cell => cell !== ''));

            setCsvData({ headers, rows: cleanedRows });

            const newInitialMapping = {};
            equipmentSchemaFields.forEach(field => {
              const foundHeader = headers.find(h =>
                h.replace(/\s+/g, '_').includes(field.toLowerCase())
              );
              newInitialMapping[field] = foundHeader || '';
            });

            setMapping(newInitialMapping);
            setIsImportDialogOpen(false);
            setIsMappingDialogOpen(true);
            setActiveStep(1);
          } else {
            toast.error(
              'CSV file must contain headers and at least one data row.'
            );
          }
        },
        error: error => {
          toast.error('Error parsing CSV file: ' + error.message);
        },
      });
    } else {
      toast.error('Please upload a valid .csv file.');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileLoaded = fileContent => {
    // Normalize line endings and split into lines
    const allLines = fileContent.replace(/\r\n|\r|\n/g, '\n').split('\n');

    // Filter out empty or whitespace-only lines
    const nonEmptyLines = allLines.filter(line => line.trim() !== '');

    if (nonEmptyLines.length < 2) {
      toast.error('CSV file must contain headers and at least one data row.');
      return;
    }

    const headers = nonEmptyLines[0]
      .split(',')
      .map(header => header.trim().toLowerCase());
    const rows = nonEmptyLines
      .slice(1)
      .map(line => line.split(',').map(cell => cell.trim()));

    if (rows.length === 0) {
      toast.error('CSV file does not contain any data rows after headers.');
      return;
    }

    setCsvData({ headers, rows });

    const newInitialMapping = {};
    equipmentSchemaFields.forEach(field => {
      const foundHeader = headers.find(h =>
        h.replace(/\s+/g, '_').includes(field.toLowerCase())
      );
      newInitialMapping[field] = foundHeader || '';
    });

    setMapping(newInitialMapping);
    setIsImportDialogOpen(false);
    setIsMappingDialogOpen(true);
    setActiveStep(1); // Move to mapping step
  };

  const handleMappingSave = finalMapping => {
    setMapping(finalMapping);
    setIsMappingDialogOpen(false);
    setActiveStep(2); // Move to results step
    // Start the import process
    handleBulkImportEquipment(finalMapping);
  };

  const findUserIdByEmail = email => {
    if (!email) return null;
    const foundUser = allUsersData.find(
      u => u.email && u.email.toLowerCase() === email.toLowerCase()
    );
    return foundUser ? foundUser.id : null;
  };

  const handleBulkImportEquipment = async currentMapping => {
    if (!user || !user.access) {
      toast.error('Authentication token not found. Please log in again.');
      setIsLoading(false);
      return;
    }
    if (!user.user || typeof user.user.TL === 'undefined') {
      toast.error("Current user's Tinkerer's Lab ID (TL) is missing.");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setLoadingMessage('Starting equipment import...');
    setErrorEntries([]);
    setSuccessfulImports(0);
    setTotalEntries(csvData.rows.length);

    let currentSuccessCount = 0;
    const errors = [];

    for (let i = 0; i < csvData.rows.length; i++) {
      const row = csvData.rows[i];
      const entry = {};

      setLoadingMessage(`Processing row ${i + 1}/${csvData.rows.length}...`);

      try {
        equipmentSchemaFields.forEach(field => {
          const csvHeader = currentMapping[field];
          const headerIndex = csvData.headers.indexOf(csvHeader);
          if (
            csvHeader &&
            headerIndex !== -1 &&
            row[headerIndex] !== undefined &&
            row[headerIndex] !== ''
          ) {
            entry[field] = row[headerIndex];
          } else if (equipmentSchemaDescription[field]?.mandatory) {
            throw new Error(
              `Mandatory field '${
                equipmentSchemaDescription[field].desc
              }' is missing or not mapped for row ${i + 1}.`
            );
          }
        });

        // Data transformation and validation
        if (entry.instances) {
          entry.instances = parseInt(entry.instances);
          if (isNaN(entry.instances))
            throw new Error(`Instances must be a number for row ${i + 1}.`);
        }

        // Validate and map availability
        if (entry.availability) {
          const availabilityKey = entry.availability.toLowerCase();
          if (!availabilityMapping[availabilityKey]) {
            throw new Error(
              `Invalid availability '${entry.availability}' for row ${
                i + 1
              }. Valid options: ${Object.keys(availabilityMapping).join(', ')}`
            );
          }
        }

        // Validate and map status
        if (entry.status) {
          const statusKey = entry.status.toLowerCase();
          if (!statusMapping[statusKey]) {
            throw new Error(
              `Invalid status '${entry.status}' for row ${i + 1}. Valid options: ${Object.keys(
                statusMapping
              ).join(', ')}`
            );
          }
        }

        // Convert supervised to boolean (matching MachineImport logic)
        if (typeof entry.supervised === 'string') {
          entry.supervised = entry.supervised.toLowerCase() === 'true';
        } else if (typeof entry.supervised === 'boolean') {
          // already boolean
        } else {
          entry.supervised = false; // default to false if not specified
        }

        if (entry.purchase_cost) {
          entry.purchase_cost = parseFloat(entry.purchase_cost);
          if (isNaN(entry.purchase_cost))
            throw new Error(`Purchase cost must be a number for row ${i + 1}.`);
        }

        // Handle Category - create new category if it doesn't exist
        if (
          entry.category &&
          entry.category.trim() !== '' &&
          !localCategories.includes(entry.category)
        ) {
          try {
            await InventoryCategoryAPIs.CategoryListPost(
              entry.category,
              user.access
            );
            setLocalCategories(prevCategories => [
              ...prevCategories,
              entry.category,
            ]);
            console.log(`Created new category: ${entry.category}`);
          } catch (error) {
            if (
              error.response?.status === 400 ||
              error.response?.status === 409
            ) {
              // Ignore error if category already exists
              console.log(
                `Category '${entry.category}' already exists, ignoring error.`
              );
              // Add to local categories list to avoid future API calls
              setLocalCategories(prevCategories => [
                ...prevCategories,
                entry.category,
              ]);
            } else {
              throw new Error(
                `Failed to create category '${entry.category}': ${error.message}`
              );
            }
          }
        }

        // Handle Location - create new location if it doesn't exist
        if (
          entry.location &&
          entry.location.trim() !== '' &&
          !localLocations.includes(entry.location)
        ) {
          try {
            await InventoryLocationAPIs.LocationListPost(
              entry.location,
              user.access
            );
            setLocalLocations(prevLocations => [
              ...prevLocations,
              entry.location,
            ]);
            console.log(`Created new location: ${entry.location}`);
          } catch (error) {
            if (
              error.response?.status === 400 ||
              error.response?.status === 409
            ) {
              // Ignore error if location already exists
              console.log(
                `Location '${entry.location}' already exists, ignoring error.`
              );
              // Add to local locations list to avoid future API calls
              setLocalLocations(prevLocations => [
                ...prevLocations,
                entry.location,
              ]);
            } else {
              throw new Error(
                `Failed to create location '${entry.location}': ${error.message}`
              );
            }
          }
        }

        // Construct FormData payload for API
        const formData = new FormData();
        formData.append('name', entry.name || '');
        formData.append('description', entry.description || '');
        formData.append('upc', entry.upc || '');
        formData.append('instances', entry.instances?.toString() || '0');
        formData.append('category', entry.category || '');
        formData.append('location', entry.location || '');
        formData.append('manufacturer', entry.manufacturer || '');
        formData.append(
          'purchase_cost',
          entry.purchase_cost?.toString() || '0'
        );
        formData.append('purchase_date', entry.purchase_date || '');
        formData.append('warranty_expiration', entry.warranty_expiration || '');

        // Add availability as index (0-4)
        if (entry.availability) {
          const availabilityKey = entry.availability.toLowerCase();
          formData.append('availability', entry.availability);
          formData.append(
            'availability1',
            availabilityMapping[availabilityKey]
          );
        }

        // Add status as index (0-2)
        if (entry.status) {
          const statusKey = entry.status.toLowerCase();
          formData.append('status', entry.status);
          formData.append('status1', statusMapping[statusKey]);
        } else {
          formData.append('status', '0');
        }

        // Add supervised as boolean
        formData.append('supervised', entry.supervised);

        console.log('Token :-', user.access);
        currentSuccessCount++;
        setSuccessfulImports(prev => prev + 1);
      } catch (err) {
        console.error('Error processing row:', i + 1, err, entry);

        // Extract proper Axios validation error
        const errorMessage = extractBulkInventoryValidationErrorMessage(err);

        errors.push({
          row: i + 1,
          data: { ...entry, name: entry.name || 'N/A' },
          error: errorMessage,
        });
      }
    }

    setErrorEntries(errors);
    setIsLoading(false);
    setLoadingMessage('');
    setIsResultDialogOpen(true);

    // Notify parent component to refresh equipment list after import
    if (onImportComplete && typeof onImportComplete === 'function') {
      onImportComplete();
    }
  };

  // Calculate completion status for each required field
  const getMappingCompletionStatus = () => {
    let mandatoryFieldsMapped = 0;
    let totalMandatoryFields = 0;

    Object.keys(equipmentSchemaDescription).forEach(field => {
      if (equipmentSchemaDescription[field].mandatory) {
        totalMandatoryFields++;
        if (mapping[field] && mapping[field] !== '') {
          mandatoryFieldsMapped++;
        }
      }
    });

    return {
      complete: mandatoryFieldsMapped === totalMandatoryFields,
      mandatoryFieldsMapped,
      totalMandatoryFields,
      percentage:
        totalMandatoryFields > 0
          ? (mandatoryFieldsMapped / totalMandatoryFields) * 100
          : 0,
    };
  };

  // Function to download sample CSV
  const downloadSampleCSV = () => {
    // Header row using equipmentSchemaFields
    const headers = equipmentSchemaFields.join(',');
    // Sample data row with availability and supervised fields
    const sampleData =
      '3D Printer,123456789,Ultimaker,High-end 3D printer,Main Lab,5,Moderate,Operational,TRUE,5000,3D Printing,2023-01-15,2025-01-15';

    // Combine header and data
    const csvContent = `${headers}\n${sampleData}`;

    // Create a blob with the CSV content
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    // Create a temporary URL to the blob
    const url = URL.createObjectURL(blob);

    // Create a temporary link element
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'equipment_import_template.csv');
    link.style.visibility = 'hidden';

    // Append to document, click and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Release the blob URL
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {/* Import Button */}
      <Button
        variant="contained"
        color="primary"
        onClick={handleOpenImportDialog}
        startIcon={<CloudUploadIcon />}
        sx={{
          display: 'flex',
          flex: { xs: 1, md: 'none' },
          flexDirection: 'row',
          minHeight: '54px',
          padding: '12px 16px',
          borderRadius: '12px',
          backgroundColor: '#FAFAFA',
          color: '#000',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid #E6E6E6',
          gap: '8px',
          textTransform: 'none',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginLeft: '16px !important',
          '& .MuiButton-startIcon': {
            margin: 0,
          },
          '&:hover': {
            backgroundColor: '#FAFAFA',
            boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
            borderColor: '#E6E6E6',
          },
        }}
      >
        <Typography variant="subtitle1" display={{ xs: 'none', lg: 'block' }}>
          Import Equipment{' '}
        </Typography>
      </Button>

      {/* Import Dialog with Stepper */}
      <Dialog
        open={isImportDialogOpen}
        onClose={() => {
          if (!isLoading) handleCloseImportDialog();
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: '10px' },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 1 }}>
          <Typography variant="h6" fontWeight={500}>
            Import Equipment from CSV
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ mt: 2, pt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '5px' }}>
            <InfoOutlined fontSize="small" color="info" sx={{ mt: '2px' }} />
            <Typography variant="body1" color="info.main">
              We recommend using only the system-generated CSV template for bulk
              inventory import. Uploading custom or modified CSV files may cause
              errors or data mismatches. Ensure that you download the latest
              template before each upload.
            </Typography>
          </Box>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ my: 3 }}>
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="body1" sx={{ mr: 1 }}>
              Upload a .csv file with equipment data
            </Typography>
            <Tooltip title="View Schema Information">
              <IconButton
                size="small"
                onClick={() => setIsInfoDialogOpen(true)}
                color="primary"
              >
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              p: 3,
              textAlign: 'center',
              backgroundColor: '#f5f9ff',
              borderRadius: '8px',
              borderStyle: 'dashed',
              borderWidth: '1px',
              borderColor: '#2196f3',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: '#edf5fe',
              },
            }}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            <CloudUploadIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="body1" gutterBottom>
              Click to browse or drag & drop your CSV file here
            </Typography>
            <Typography variant="caption" color="textSecondary">
              File should contain headers and equipment data
            </Typography>
            <input
              type="file"
              accept=".csv"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </Paper>

          <Box
            sx={{
              mt: 2,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <Chip
              icon={<InfoIcon />}
              label="Need a template?"
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => setIsInfoDialogOpen(true)}
              sx={{ mr: 1 }}
            />
            <Chip
              icon={<DownloadIcon />}
              label="Download CSV Template"
              variant="outlined"
              color="secondary"
              size="small"
              onClick={downloadSampleCSV}
              sx={{ mr: 1 }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid #eee', p: 2 }}>
          <Button
            onClick={handleCloseImportDialog}
            variant="outlined"
            color="inherit"
            disabled={isLoading}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mapping Dialog */}
      <Dialog
        open={isMappingDialogOpen}
        onClose={() => !isLoading && setIsMappingDialogOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: '10px' },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6" fontWeight={500}>
              Map CSV Columns to Equipment Fields
            </Typography>
            <Chip
              label={`${getMappingCompletionStatus().mandatoryFieldsMapped}/${
                getMappingCompletionStatus().totalMandatoryFields
              } Required Fields Mapped`}
              color={
                getMappingCompletionStatus().complete ? 'success' : 'warning'
              }
              size="small"
            />
          </Box>
        </DialogTitle>

        <DialogContent sx={{ mt: 2, pt: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="body1">
              Match your CSV column headers to the required equipment fields
            </Typography>
            <Tooltip title="View Schema Information">
              <IconButton
                size="small"
                onClick={() => setIsInfoDialogOpen(true)}
                color="primary"
              >
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: '#f5f9ff',
              borderRadius: '8px',
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Preview: {csvData.rows.length} rows found in CSV
            </Typography>
            <Box
              sx={{
                maxHeight: '100px',
                overflow: 'auto',
                backgroundColor: '#fff',
                p: 1,
                borderRadius: '4px',
                border: '1px solid #e0e0e0',
                fontSize: '0.875rem',
              }}
            >
              <Typography
                component="div"
                variant="body2"
                sx={{ fontFamily: 'monospace', whiteSpace: 'pre' }}
              >
                {csvData.headers.join(', ')}
              </Typography>
              <Divider sx={{ my: 0.5 }} />
              {csvData.rows.slice(0, 2).map((row, idx) => (
                <Typography
                  key={idx}
                  component="div"
                  variant="body2"
                  sx={{ fontFamily: 'monospace', whiteSpace: 'pre' }}
                >
                  {row.join(', ')}
                </Typography>
              ))}
              {csvData.rows.length > 2 && (
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', fontStyle: 'italic' }}
                >
                  ... and {csvData.rows.length - 2} more rows
                </Typography>
              )}
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: '8px',
              border: '1px solid #eee',
              maxHeight: '50vh',
              overflow: 'auto',
            }}
          >
            <Grid container spacing={2}>
              <Grid
                item
                xs={12}
                sx={{ borderBottom: '1px solid #eee', pb: 1, mb: 1 }}
              >
                <Grid container>
                  <Grid item xs={5}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      Equipment Field
                    </Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      CSV Header
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>

              {equipmentSchemaFields.map(field => (
                <Grid
                  item
                  xs={12}
                  key={field}
                  sx={{
                    py: 0.5,
                    backgroundColor: equipmentSchemaDescription[field]
                      ?.mandatory
                      ? '#f8f8f8'
                      : 'transparent',
                  }}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={5}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {equipmentSchemaDescription[field]?.mandatory && (
                          <Typography sx={{ color: 'error.main', mr: 0.5 }}>
                            *
                          </Typography>
                        )}
                        <Typography>
                          {equipmentSchemaDescription[field]?.desc || field}
                        </Typography>
                        <Tooltip
                          title={
                            equipmentSchemaDescription[field]?.options || ''
                          }
                        >
                          <IconButton size="small">
                            <InfoIcon fontSize="small" color="action" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Grid>
                    <Grid item xs={7}>
                      <FormControl
                        fullWidth
                        size="small"
                        sx={{ backgroundColor: 'white' }}
                      >
                        <Select
                          value={mapping[field] || ''}
                          onChange={e =>
                            setMapping(prev => ({
                              ...prev,
                              [field]: e.target.value,
                            }))
                          }
                          displayEmpty
                          sx={{ borderRadius: '8px' }}
                          startAdornment={
                            mapping[field] ? (
                              <CheckCircleOutlineIcon
                                fontSize="small"
                                color="success"
                                sx={{ mr: 1, ml: 1 }}
                              />
                            ) : equipmentSchemaDescription[field]?.mandatory ? (
                              <ErrorOutlineIcon
                                fontSize="small"
                                color="error"
                                sx={{ mr: 1, ml: 1 }}
                              />
                            ) : null
                          }
                        >
                          <MenuItem value="">
                            <em>Not mapped</em>
                          </MenuItem>
                          {csvData.headers.map(header => (
                            <MenuItem
                              key={header}
                              value={header}
                              disabled={
                                Object.values(mapping).includes(header) &&
                                mapping[field] !== header
                              }
                            >
                              {header}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: '1px solid #eee',
            p: 2,
            justifyContent: 'space-between',
          }}
        >
          <Button
            onClick={() => {
              setIsMappingDialogOpen(false);
              setIsImportDialogOpen(true);
              setActiveStep(0);
            }}
            startIcon={<ArrowBackIcon />}
            disabled={isLoading}
          >
            Back
          </Button>
          <Box>
            <Button
              onClick={() => setIsMappingDialogOpen(false)}
              color="inherit"
              sx={{ mr: 1 }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleMappingSave(mapping)}
              variant="contained"
              color="primary"
              disabled={!getMappingCompletionStatus().complete || isLoading}
              endIcon={<ArrowForwardIcon />}
            >
              Import Equipment
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Result Dialog */}
      <Dialog
        open={isResultDialogOpen}
        onClose={() => setIsResultDialogOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: '10px' },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 1 }}>
          <Typography variant="h6" fontWeight={500}>
            Import Results
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ mt: 2, pt: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Paper
            variant="outlined"
            sx={{
              p: 3,
              textAlign: 'center',
              borderRadius: '8px',
              backgroundColor: successfulImports > 0 ? '#f0f7f0' : '#fff',
              mb: 3,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 2,
              }}
            >
              {successfulImports > 0 ? (
                <CheckCircleOutlineIcon
                  sx={{ fontSize: 48, color: 'success.main', mr: 2 }}
                />
              ) : (
                <ErrorOutlineIcon
                  sx={{ fontSize: 48, color: 'warning.main', mr: 2 }}
                />
              )}
              <Typography variant="h5" component="div">
                {successfulImports} of {totalEntries} equipment imported
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 2,
              }}
            >
              <Chip
                label={`${successfulImports} Successful`}
                color="success"
                variant="outlined"
                icon={<CheckCircleOutlineIcon />}
              />
              {errorEntries.length > 0 && (
                <Chip
                  label={`${errorEntries.length} Failed`}
                  color="error"
                  variant="outlined"
                  icon={<ErrorOutlineIcon />}
                />
              )}
            </Box>
          </Paper>

          {errorEntries.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
              <Typography
                variant="subtitle1"
                color="error"
                fontWeight={500}
                gutterBottom
              >
                Errors ({errorEntries.length})
              </Typography>
              <Box
                sx={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  border: '1px solid #eee',
                  borderRadius: '4px',
                }}
              >
                {errorEntries.map(err => (
                  <Box
                    key={err.row}
                    sx={{
                      p: 1.5,
                      borderBottom: '1px solid #eee',
                      '&:last-child': { borderBottom: 'none' },
                      backgroundColor: '#fff5f5',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="subtitle2">
                        Row {err.row}: {err.data.name || 'Untitled Equipment'}
                      </Typography>
                    </Box>
                    <Typography
                      color="error.main"
                      variant="body2"
                      sx={{ fontWeight: 500 }}
                    >
                      {err.error}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          )}
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid #eee', p: 2 }}>
          <Button
            onClick={() => setIsResultDialogOpen(false)}
            variant="contained"
            color="primary"
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schema Info Dialog */}
      <Dialog
        open={isInfoDialogOpen}
        onClose={() => setIsInfoDialogOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: '10px' },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 1 }}>
          <Typography variant="h6" fontWeight={500}>
            Equipment CSV Schema Information
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography gutterBottom>
              The following fields are expected for equipment import. Fields
              marked with an asterisk (*) are mandatory.
            </Typography>

            <Paper
              variant="outlined"
              sx={{ mt: 2, borderRadius: '8px', overflow: 'hidden' }}
            >
              <Box
                sx={{
                  p: 1.5,
                  backgroundColor: '#f5f5f5',
                  borderBottom: '1px solid #ddd',
                }}
              >
                <Grid container spacing={1}>
                  <Grid item xs={3}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      Field Name
                    </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      Description
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      Expected Format / Options
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ maxHeight: '50vh', overflowY: 'auto' }}>
                {equipmentSchemaFields.map((field, index) => (
                  <Box
                    key={field}
                    sx={{
                      p: 1.5,
                      borderBottom:
                        index < equipmentSchemaFields.length - 1
                          ? '1px solid #eee'
                          : 'none',
                      backgroundColor: equipmentSchemaDescription[field]
                        ?.mandatory
                        ? '#f8f8f8'
                        : 'transparent',
                    }}
                  >
                    <Grid container spacing={1}>
                      <Grid item xs={3}>
                        <Typography
                          sx={{
                            fontWeight: equipmentSchemaDescription[field]
                              ?.mandatory
                              ? 'bold'
                              : 'normal',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {field}
                          {equipmentSchemaDescription[field]?.mandatory && (
                            <Box
                              component="span"
                              sx={{ color: 'error.main', ml: 0.5 }}
                            >
                              *
                            </Box>
                          )}
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography>
                          {equipmentSchemaDescription[field]?.desc}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography>
                          {equipmentSchemaDescription[field]?.options}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Box>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                mt: 3,
                p: 2,
                borderRadius: '8px',
                backgroundColor: '#f5f9ff',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography variant="subtitle1" fontWeight={500}>
                  Sample CSV Format
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={downloadSampleCSV}
                  sx={{
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontWeight: 500,
                  }}
                >
                  Download Template
                </Button>
              </Box>
              <Box
                sx={{
                  p: 1,
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  border: '1px solid #e0e0e0',
                  overflow: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre', fontFamily: 'monospace' }}
                >
                  {equipmentSchemaFields.join(',')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre', fontFamily: 'monospace' }}
                >
                  3D Printer,123456789,Ultimaker,High-end 3D printer,Main
                  Lab,5,Operational,5000,3D Printing,2023-01-15,2025-01-15
                </Typography>
              </Box>
            </Paper>
          </Box>
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid #eee', p: 2 }}>
          <Button
            onClick={() => setIsInfoDialogOpen(false)}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Dialog */}
      <Dialog
        open={isLoading}
        PaperProps={{
          style: {
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
            backdropFilter: 'blur(4px)',
            borderRadius: '12px',
          },
        }}
      >
        <DialogContent
          sx={{
            textAlign: 'center',
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '250px',
          }}
        >
          <CircularProgress size={60} thickness={4} sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            {loadingMessage}
          </Typography>
          {totalEntries > 0 && (
            <Box sx={{ width: '100%', mt: 2 }}>
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ width: '100%', mr: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={(successfulImports / totalEntries) * 100}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {Math.round((successfulImports / totalEntries) * 100)}%
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {successfulImports}/{totalEntries} processed
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EquipmentImport;
